import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldSet, FieldLabel } from '@/components/ui/field';

import { Organization } from '@/entities/organizations';
import { FilterSelect, FormField } from '@/shared/ui';
import { SimpleSelect } from '@/shared/ui/filters';

import { useOrganizationForm } from '../model/use-organization-form';
import { OrganizationFormSkeleton } from './form-skeleton';

interface OrganizationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (organization: Organization) => void;
  organizationId: number;
  mode?: 'create' | 'edit' | 'view';
}

export const OrganizationFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  mode = 'view'
}: OrganizationFormModalProps) => {
  const { t } = useTranslation();

  const {
    form,
    typeOptions,
    countryOptions,
    isLoading,
    isLoadingCountries,
    isSaving,
    isViewMode,
    handleSubmit,
    handleClose
  } = useOrganizationForm({
    organizationId,
    mode,
    onSuccess,
    onClose
  });

  const {
    control,
    formState: { errors },
    register
  } = form;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}>
        <form
          id="organization-modal-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          className="py-4">
          {isLoading ? (
            <OrganizationFormSkeleton />
          ) : (
            <div className="space-y-6">
              {/* Basic Information Section */}
              <FieldSet>
                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <FormField
                    label={t('org.name.label')}
                    name="name"
                    error={!isViewMode ? errors.name?.message : undefined}
                    required>
                    <Input {...register('name')} id="name" disabled={isViewMode} />
                  </FormField>

                  <FormField
                    label={t('org.type.label')}
                    name="type"
                    error={!isViewMode ? errors.type?.message : undefined}
                    required>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <FilterSelect
                          options={typeOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('org.type.label')}
                          disabled={isViewMode}
                          width="full"
                        />
                      )}
                    />
                  </FormField>
                </FieldGroup>

                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <FormField
                    label={t('org.abrv.label')}
                    name="abbreviation"
                    error={!isViewMode ? errors.abbreviation?.message : undefined}
                    required>
                    <Input {...register('abbreviation')} id="abbreviation" disabled={isViewMode} />
                  </FormField>

                  <FormField
                    label={t('org.country.label')}
                    name="countryId"
                    error={!isViewMode ? errors.countryId?.message : undefined}>
                    <Controller
                      name="countryId"
                      control={control}
                      render={({ field }) => (
                        <SimpleSelect
                          options={countryOptions}
                          value={field.value?.toString()}
                          onChange={(value) => field.onChange(value ? Number(value) : undefined)}
                          isLoading={isLoadingCountries}
                          disabled={isViewMode}
                          placeholder={t('org.country.label')}
                          className="w-full"
                        />
                      )}
                    />
                  </FormField>
                </FieldGroup>
              </FieldSet>

              {/* Website Information Section */}
              <FieldSet>
                <FormField
                  label={t('org.website.label')}
                  name="website"
                  error={!isViewMode ? errors.website?.message : undefined}>
                  <Input
                    {...register('website')}
                    id="website"
                    disabled={isViewMode}
                    placeholder="e.g. www.goal.ie or https://example.com"
                  />
                </FormField>

                <FormField
                  label={t('org.wingsIdnf.label')}
                  name="wingsId"
                  error={!isViewMode ? errors.wingsId?.message : undefined}>
                  <Input {...register('wingsId')} id="wingsId" disabled={isViewMode} />
                </FormField>
              </FieldSet>

              {/* Organization Flags Section */}
              <FieldSet>
                <FieldGroup className="grid sm:grid-cols-2 gap-4">
                  <Field orientation="horizontal">
                    <Controller
                      name="isDonor"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="isDonor"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isViewMode}
                        />
                      )}
                    />
                    <FieldLabel
                      htmlFor="isDonor"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t('org.isDonor.label')}
                    </FieldLabel>
                  </Field>

                  <Field orientation="horizontal">
                    <Controller
                      name="isClearingAgent"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="isClearingAgent"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled
                        />
                      )}
                    />
                    <FieldLabel
                      htmlFor="isClearingAgent"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t('org.isAgent.label')}
                    </FieldLabel>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
          )}

          <div className="flex justify-center gap-3 pt-6 mt-6 border-t">
            {isViewMode ? (
              <ActionButton intent="dismiss" type="button" onClick={handleClose} className="min-w-[120px]">
                {t('gen.cancel.label', 'Close Dialog')}
              </ActionButton>
            ) : (
              <>
                <ActionButton
                  type="button"
                  intent="dismiss"
                  onClick={handleClose}
                  className="min-w-[120px]"
                  disabled={isSaving}>
                  {t('gen.cancel.label', 'Close Dialog')}
                </ActionButton>
                <ActionButton
                  intent="primary"
                  type="submit"
                  className="min-w-[120px]"
                  disabled={isSaving}
                  form="organization-modal-form">
                  {isSaving ? t('common.saving', 'Saving...') : t('newcmr.save.label')}
                </ActionButton>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
